import type { CartItem } from '~/types'

const CART_STORAGE_KEY = 'granados-quote-cart'

// Singleton to ensure cart is only hydrated once across the app
let cartHydrationPromise: Promise<void> | null = null

export function useQuoteCart() {
  // Use useState for SSR-safe reactive state
  const items = useState<CartItem[]>('quote-cart', () => [])
  const isHydrated = useState('cart-hydrated', () => false)

  // Load cart from localStorage on client - singleton pattern
  const loadCart = () => {
    if (import.meta.client && !isHydrated.value && !cartHydrationPromise) {
      cartHydrationPromise = new Promise((resolve) => {
        try {
          const stored = localStorage.getItem(CART_STORAGE_KEY)
          if (stored) {
            const parsed = JSON.parse(stored)
            // Validate cart structure
            if (Array.isArray(parsed)) {
              items.value = parsed
            }
          }
        } catch (e) {
          console.error('Error loading cart:', e)
          // Clear corrupted data
          localStorage.removeItem(CART_STORAGE_KEY)
        } finally {
          isHydrated.value = true
          resolve()
        }
      })
    }
    return cartHydrationPromise || Promise.resolve()
  }

  // Save cart to localStorage with error handling
  const saveCart = () => {
    if (import.meta.client && isHydrated.value) {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items.value))
      } catch (e) {
        console.error('Error saving cart:', e)
        // Handle quota exceeded error
        if (e instanceof Error && e.name === 'QuotaExceededError') {
          console.warn('LocalStorage quota exceeded. Cart may not persist.')
        }
      }
    }
  }

  // Watch for changes and persist (only when hydrated)
  watch(items, () => {
    if (isHydrated.value) {
      saveCart()
    }
  }, { deep: true })

  // Initialize on mount
  onMounted(() => {
    loadCart()
  })

  // Add item to cart with validation
  const addItem = (item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    if (quantity <= 0) {
      console.warn('Cannot add item with quantity <= 0')
      return
    }

    const existingIndex = items.value.findIndex(i => i.productId === item.productId)

    if (existingIndex >= 0) {
      items.value[existingIndex].quantity += quantity
    } else {
      items.value.push({
        ...item,
        quantity
      })
    }
  }

  // Update item quantity with validation
  const updateQuantity = (productId: string, quantity: number) => {
    const index = items.value.findIndex(i => i.productId === productId)
    if (index >= 0) {
      if (quantity <= 0) {
        items.value.splice(index, 1)
      } else {
        items.value[index].quantity = Math.max(1, Math.floor(quantity))
      }
    }
  }

  // Remove item from cart
  const removeItem = (productId: string) => {
    const index = items.value.findIndex(i => i.productId === productId)
    if (index >= 0) {
      items.value.splice(index, 1)
    }
  }

  // Clear entire cart
  const clearCart = () => {
    items.value = []
  }

  // Check if product is in cart (memoized)
  const isInCart = (productId: string): boolean => {
    return items.value.some(i => i.productId === productId)
  }

  // Get quantity of specific product (memoized)
  const getQuantity = (productId: string): number => {
    const item = items.value.find(i => i.productId === productId)
    return item?.quantity ?? 0
  }

  // Get cart item by product ID
  const getItem = (productId: string): CartItem | undefined => {
    return items.value.find(i => i.productId === productId)
  }

  // Computed properties
  const totalItems = computed(() => {
    return items.value.reduce((sum, item) => sum + item.quantity, 0)
  })

  const isEmpty = computed(() => items.value.length === 0)

  const itemCount = computed(() => items.value.length)

  return {
    items: readonly(items),
    totalItems,
    itemCount,
    isEmpty,
    isHydrated: readonly(isHydrated),
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    isInCart,
    getQuantity,
    getItem,
    loadCart
  }
}
